import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Grid, Card, CardContent, Divider, Chip, Tooltip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { ArrowLeft, Edit, MapPin, Globe, Phone, Mail, Building2, User, Users, School } from 'lucide-react';
import { useOrganizationStore } from '@/store/superAdmin/organizationStore';
import { PageHeader } from '@/components/navigation/PageHeader';
import { Loader } from '@/components/feedback/Loader';
import { ErrorState } from '@/components/feedback/ErrorState';
import { tokens } from '@/theme/tokens';

export function OrganizationDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { selectedOrganization: org, loading, error, fetchOrganizationById } = useOrganizationStore();

  useEffect(() => {
    if (id) {
      fetchOrganizationById(id);
    }
  }, [id, fetchOrganizationById]);

  if (loading || !org) return <Loader message="Loading organization details..." />;
  if (error) return <ErrorState message={error} onRetry={() => fetchOrganizationById(id)} />;

  const owner = org.users?.find(u => u.role?.name === 'ORGANIZATION_ADMIN') || org.users?.find(u => u.role?.name === 'SCHOOL_ADMIN') || org.users?.find(u => u.role?.name === 'OWNER') || org.users?.[0] || {};
  const ownerName = [owner.first_name, owner.last_name].filter(Boolean).join(' ') || owner.name || 'N/A';
  const ownerEmail = owner.email || 'N/A';
  const ownerPhone = owner.phone || 'N/A';

  const userColumns = [
    { 
      field: 'name', 
      headerName: 'Name', 
      flex: 1.2, 
      minWidth: 160,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={500}>
          {[params.row.first_name, params.row.last_name].filter(Boolean).join(' ') || params.row.name || 'N/A'}
        </Typography>
      )
    },
    { 
      field: 'role', 
      headerName: 'Role', 
      flex: 1, 
      minWidth: 140,
      renderCell: (params) => (
        <Chip
          label={params.row.role?.name || 'User'}
          size="small"
          sx={{
            backgroundColor: tokens.colors.primary[50],
            color: tokens.colors.primary.main,
            fontWeight: 500,
            fontSize: '0.75rem',
            borderRadius: tokens.radius.sm,
          }}
        />
      )
    },
    {
      field: 'school',
      headerName: 'Assigned School',
      flex: 1.3,
      minWidth: 180,
      renderCell: (params) => {
        const schoolId = params.row.school_id;
        if (!schoolId) {
          return (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', bgcolor: tokens.colors.grey[100], px: 1, py: 0.5, borderRadius: tokens.radius.sm }}>
              Organization Level
            </Typography>
          );
        }
        const matchedSchool = org?.schools?.find((s) => s.id === schoolId);
        return (
          <Typography variant="body2" fontWeight={500} color="text.primary">
            {matchedSchool ? matchedSchool.school_name : 'Unknown School'}
          </Typography>
        );
      },
    },
    { 
      field: 'email', 
      headerName: 'Email', 
      flex: 1.5, 
      minWidth: 220,
      renderCell: (params) => (
        <Tooltip title={params.value || ''} placement="top" arrow>
          <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
            {params.value}
          </Typography>
        </Tooltip>
      )
    },
    { field: 'phone', headerName: 'Phone', flex: 1, minWidth: 130 },
    { 
      field: 'status', 
      headerName: 'Status', 
      flex: 0.8, 
      minWidth: 90,
      renderCell: (params) => (
        <Chip
          label={params.row.status?.toUpperCase() === 'ACTIVE' ? 'Active' : 'Inactive'}
          size="small"
          sx={{
            backgroundColor: params.row.status?.toUpperCase() === 'ACTIVE' ? tokens.colors.success[50] : tokens.colors.error[50],
            color: params.row.status?.toUpperCase() === 'ACTIVE' ? tokens.colors.success.main : tokens.colors.error.main,
            fontWeight: 600,
            borderRadius: tokens.radius.sm,
          }}
        />
      )
    },
  ];

  return (
    <Box sx={{ pb: 4 }}>
      <PageHeader
        title={org.organization_name || org.name}
        subtitle={`Organization Details & Information`}
        actions={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowLeft size={18} />}
              onClick={() => navigate('/super-admin/organizations')}
              sx={{
                borderRadius: tokens.radius.sm,
                fontWeight: 600,
                textTransform: 'none',
                borderColor: tokens.colors.border,
                color: tokens.colors.text.primary,
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              startIcon={<Edit size={18} />}
              onClick={() => navigate(`/super-admin/organizations/edit/${org.id}`)}
              sx={{
                borderRadius: tokens.radius.sm,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: 'none',
              }}
            >
              Edit Details
            </Button>
          </Box>
        }
      />

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card elevation={0} sx={{ borderRadius: tokens.radius.md, border: `1px solid ${tokens.colors.border}`, mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Typography variant="h6" fontWeight={600} sx={{ color: tokens.colors.primary.main, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Building2 size={20} /> General Information
                </Typography>
                <Chip
                  label={org.status?.toUpperCase() === 'ACTIVE' ? 'Active' : 'Inactive'}
                  size="small"
                  sx={{
                    backgroundColor: org.status?.toUpperCase() === 'ACTIVE' ? tokens.colors.success[50] : tokens.colors.error[50],
                    color: org.status?.toUpperCase() === 'ACTIVE' ? tokens.colors.success.main : tokens.colors.error.main,
                    fontWeight: 600,
                    borderRadius: tokens.radius.sm,
                  }}
                />
              </Box>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, color: tokens.colors.text.secondary, mb: 1 }}>
                    <Mail size={18} />
                    <Typography variant="body2">{org.email}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, color: tokens.colors.text.secondary, mb: 1 }}>
                    <Phone size={18} />
                    <Typography variant="body2">{org.phone}</Typography>
                  </Box>
                  {(org.primary_domain || org.primaryDomain) && (
                    <Box sx={{ display: 'flex', gap: 1.5, color: tokens.colors.text.secondary, mb: 1 }}>
                      <Globe size={18} />
                      <Typography variant="body2">
                        <strong>Primary Domain:</strong> {org.primary_domain || org.primaryDomain}
                      </Typography>
                    </Box>
                  )}
                  {org.website && (
                    <Box sx={{ display: 'flex', gap: 1.5, color: tokens.colors.text.secondary }}>
                      <Globe size={18} />
                      <Typography variant="body2" component="a" href={org.website} target="_blank" sx={{ color: tokens.colors.info.main, textDecoration: 'none' }}>
                        {org.website}
                      </Typography>
                    </Box>
                  )}
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', gap: 1.5, color: tokens.colors.text.secondary }}>
                    <MapPin size={18} sx={{ mt: 0.5, flexShrink: 0 }} />
                    <Typography variant="body2">
                      {org.address}<br />
                      {org.city}, {org.state} {org.postal_code || org.postalCode}<br />
                      {org.country}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ borderRadius: tokens.radius.md, border: `1px solid ${tokens.colors.border}` }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 3, color: tokens.colors.primary.main, display: 'flex', alignItems: 'center', gap: 1 }}>
                <School size={20} /> Schools Under Organization
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Total Schools: {org.schools?.length || 0}
              </Typography>
              
              {org.schools && org.schools.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                  {org.schools.map((school) => (
                    <Box 
                      key={school.id} 
                      sx={{ 
                        p: 2, 
                        border: `1px solid ${tokens.colors.border}`, 
                        borderRadius: tokens.radius.sm,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: `${tokens.colors.primary.main}08`
                        }
                      }}
                      onClick={() => navigate(`/super-admin/schools/${school.id}`)}
                    >
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                          {school.school_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <MapPin size={12} /> {school.city}, {school.state}
                        </Typography>
                      </Box>
                      <Chip
                        label={school.status?.toUpperCase() === 'ACTIVE' ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          backgroundColor: school.status?.toUpperCase() === 'ACTIVE' ? tokens.colors.success[50] : tokens.colors.error[50],
                          color: school.status?.toUpperCase() === 'ACTIVE' ? tokens.colors.success.main : tokens.colors.error.main,
                          fontWeight: 600,
                          borderRadius: tokens.radius.sm,
                          height: 20,
                          fontSize: '0.7rem'
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}

              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate(`/super-admin/schools?orgId=${org.id}`)}
                sx={{ borderRadius: tokens.radius.sm, textTransform: 'none' }}
              >
                View All Schools
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex' }}>
          <Card elevation={0} sx={{ width: '100%', height: '100%', borderRadius: tokens.radius.md, border: `1px solid ${tokens.colors.border}` }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 3, color: tokens.colors.primary.main, display: 'flex', alignItems: 'center', gap: 1 }}>
                <User size={20} /> Owner Details
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block">Name</Typography>
                <Typography variant="body2" fontWeight={500}>{ownerName}</Typography>
              </Box>
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block">Email</Typography>
                <Typography variant="body2" fontWeight={500}>{ownerEmail}</Typography>
              </Box>
              <Divider sx={{ my: 1.5 }} />
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">Phone</Typography>
                <Typography variant="body2" fontWeight={500}>{ownerPhone}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3 }}>
        <Card elevation={0} sx={{ borderRadius: tokens.radius.md, border: `1px solid ${tokens.colors.border}` }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 3, color: tokens.colors.primary.main, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Users size={20} /> Organization Users
            </Typography>
            
            {org.users && org.users.length > 0 ? (
              <Box sx={{ width: '100%', mt: 2 }}>
                <DataGrid
                  autoHeight
                  rows={org.users}
                  columns={userColumns}
                  pageSizeOptions={[5, 10, 25]}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 5 } },
                  }}
                  disableRowSelectionOnClick
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell': {
                      borderColor: tokens.colors.border,
                      display: 'flex',
                      alignItems: 'center',
                    },
                    '& .MuiDataGrid-cell:focus': { outline: 'none' },
                    '& .MuiDataGrid-row:hover': { backgroundColor: `${tokens.colors.primary.main}08` },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: tokens.colors.background.default,
                      borderBottom: `1px solid ${tokens.colors.border}`,
                    },
                  }}
                />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No users found for this organization.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
